<template>
  <!-- With isInsideOtherInput false the dropdown is alone in its row, and
       tw-h-8 gives it the 32px height of SfButtonDs. With isInsideOtherInput
       true the parent element sets the height and the dropdown stretches into
       it. -->
  <SfDropdown
    data-testid="workflow-filter-dropdown"
    :class="{ 'tw-h-8': !isInsideOtherInput }"
    :drawer-size="'large'"
    :drawer-margin="true"
    :dropdown-id="dropdownId"
    :is-open="isScopedDropdownOpen"
    :align-option="alignOption"
    :is-disabled="false"
    :is-inside-other-input="isInsideOtherInput"
    :menu-class-names="menuClassNames"
    :select-class-names="triggerClassNames"
    :variant="variant"
    @dropdown-clicked="handleToggleDropdown"
    @dropdown-closed="dropdownStore.closeDropdown"
  >
    <template #trigger>
      <span class="tw-whitespace-nowrap">
        <span v-if="loading" class="sf-spinner sf-spinner-s sf-inline-block" />
        <span v-else class="tw-inline-flex tw-items-center tw-gap-1">
          {{ I18n.t('ledger.carousel.all') }}:
          <span
            v-if="triggerIcon && !isSvgIcon(effectiveStatus)"
            class="icon"
            :class="triggerIcon"
          ></span>
          <span
            v-if="isSvgIcon(effectiveStatus)"
            class="
              tw-inline-flex tw-items-center tw-min-w-[14px] tw-min-h-[14px]
            "
          >
            <img :src="$imagesRequire(triggerIcon)" width="14" height="14" />
          </span>
          <span
            data-testid="workflow-status-trigger"
            class="tw-hidden 2xl:tw-inline-flex"
            >{{ triggerLabel }}</span
          >
        </span>
      </span>
    </template>

    <template #menu>
      <div class="tw-p-2">
        <SfDropdownItem
          v-for="item in elements"
          :key="item.label"
          :has-border="false"
          :is-rounded="true"
          :is-selected="item.isSelected"
          :is-short="true"
          :is-wide="true"
          :is-wrapped="false"
          @click="handleItemClick(item.value)"
        >
          <template #icon>
            <span
              v-if="item.icon && !isSvgIcon(item.value)"
              class="tw-inline-block tw-mr-1"
              :class="item.icon"
            ></span>
            <span
              v-if="isSvgIcon(item.value)"
              class="tw-inline-flex tw-items-center tw-min-w-[14px] tw-mr-1"
            >
              <img :src="$imagesRequire(item.icon)" width="14" height="14" />
            </span>
          </template>

          <template #default>
            <span
              class="
                tw-flex-1
                tw-inline-flex
                tw-items-center
                tw-justify-between
                tw-gap-2
              "
            >
              <span>
                {{ item.label }}
              </span>
              <span
                v-if="loadingCount"
                class="sf-spinner sf-spinner-primary"
              ></span>
              <span v-if="!loadingCount">
                {{
                  item.value === 'show-all'
                    ? counts.total
                    : counts[camelize(item.value)]
                }}
              </span>
            </span>
          </template>
        </SfDropdownItem>
      </div>
    </template>
  </SfDropdown>
</template>

<script lang="ts" setup>
import type {
  WorkflowFilterStatus,
  WorkflowFilterStatusWithoutShowAll,
} from '@app/shared/types/sf-data'
import type { alignOptions } from '@app/shared/types/dropdown'

import { camelize } from 'humps'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { filterData } from './constants'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'

import { useDropdown } from '@app/shared/pinia/dropdown'
import { useWorkflowStatusCount } from '@app/shared/vue/components/molecules/sf-workflow-status-dropdown/composables/use-workflow-status-count'

interface Props {
  alignOption: typeof alignOptions[keyof typeof alignOptions]
  availableStatuses: WorkflowFilterStatus[]
  defaultCountStatus?: WorkflowFilterStatus
  dropdownId: string
  isDisabled?: boolean
  isInsideOtherInput?: boolean
  ledgerId: number
  loading?: boolean
  menuClassNames?: string[]
  selectedStatus?: WorkflowFilterStatus
  variant?: 'default' | 'seamless'
  workflowId: number
}

const props = withDefaults(defineProps<Props>(), {
  availableStatuses: () => [
    'show-all',
    'todos',
    'checks',
    'unreconciled',
    'not_prepared',
    'unreviewed_by_me',
    'starred',
  ],
  defaultCountStatus: 'show-all',
  isDisabled: false,
  isInsideOtherInput: false,
  loading: false,
  menuClassNames: () => [],
  selectedStatus: 'show-all',
  variant: 'default',
})

const emit =
  defineEmits<{
    'item-clicked': [item: WorkflowFilterStatus]
  }>()

const I18n = window.I18n

const {
  counts,
  fetchFilterCounts,
  loading: loadingCount,
} = useWorkflowStatusCount({
  defaultStatus: props.defaultCountStatus,
  ledgerId: props.ledgerId,
  statuses: props.availableStatuses
    .filter((status) => status !== 'show-all')
    .map(camelize) as WorkflowFilterStatusWithoutShowAll[],
  workflowId: props.workflowId,
})

// The .sf-dropdown2-select element takes its height from the dropdown root, so
// !tw-py-0 removes the tw-py-1.5 or tw-py-[9px] that SfDropdown applies and
// flex centres the label instead.
const triggerClassNames = ['tw-flex', 'tw-items-center', '!tw-py-0']

const dropdownStore = useDropdown()

const { currentDropdownId } = storeToRefs(dropdownStore)

const elements = computed(() => {
  return props.availableStatuses.map((key) => {
    return {
      icon: filterData[key].icon,
      isSelected: effectiveStatus.value === key,
      label: I18n.t(`ledger.carousel.${key}`),
      value: key,
    }
  })
})

const isScopedDropdownOpen = computed(() => {
  return currentDropdownId.value === props.dropdownId
})

const effectiveStatus = computed(() => {
  return props.availableStatuses.includes(props.selectedStatus)
    ? props.selectedStatus
    : 'show-all'
})

const selectedItem = computed(() => {
  return filterData[effectiveStatus.value]
})

const triggerIcon = computed(() => {
  return selectedItem.value && !selectedItem.value.hideIcon
    ? selectedItem.value.icon
    : ''
})

const triggerLabel = computed(() => {
  return selectedItem.value
    ? I18n.t(`ledger.carousel.${effectiveStatus.value}`)
    : I18n.t(`ledger.carousel.show-all`)
})

function handleToggleDropdown() {
  fetchFilterCounts()
  dropdownStore.toggleDropdown(props.dropdownId)
}

const SVG_ICON_STATUSES: WorkflowFilterStatus[] = [
  'not_prepared',
  'unreviewed_by_me',
  'prepared_unreviewed_by_me',
]

function isSvgIcon(status: WorkflowFilterStatus): boolean {
  return SVG_ICON_STATUSES.includes(status)
}

function handleItemClick(item: WorkflowFilterStatus) {
  if (item !== effectiveStatus.value) {
    dropdownStore.closeDropdown()
    emit('item-clicked', item)
  }
}
</script>

<style lang="scss" scoped>
/* .sf-dropdown2-wrapper sits between the dropdown root and .sf-dropdown2-select
   and is an inline-block with no height of its own. height: 100% on
   .sf-dropdown2-select alone would resolve against the wrapper's content
   height, and the select would keep the height of its padding. */
:deep(.sf-dropdown2-wrapper),
:deep(.sf-dropdown2-select) {
  height: 100%;
}
</style>
